import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Checkbox } from '../components/ui/checkbox';
import { toast } from 'sonner';
import {
  Key,
  ArrowRight,
  ArrowLeft,
  CheckCircle2,
  Building2,
  Home,
  User,
  Lock,
  Phone,
  Bell,
  Mail,
  Shield,
  Loader2,
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function InvitationRegister() {
  const navigate = useNavigate();
  const { user, loading: authLoading, setUser } = useAuth();
  
  const [step, setStep] = useState(1); // 1: Enter code, 2: Verify & view details, 3: Set password
  const [loading, setLoading] = useState(false);
  
  // Redirect if already logged in
  useEffect(() => {
    if (!authLoading && user) {
      navigate('/dashboard', { replace: true });
    }
  }, [user, authLoading, navigate]);

  // Step 1: Code entry
  const [accessCode, setAccessCode] = useState('');
  
  // Step 2: Verified invitation details
  const [invitation, setInvitation] = useState(null);
  
  // Step 3: Registration form
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [mobileNumber, setMobileNumber] = useState('');
  const [notifications, setNotifications] = useState({
    email: true,
    push: true,
    sms: false,
  });

  const handleVerifyCode = async () => {
    if (!accessCode.trim()) {
      toast.error('Please enter your invitation code');
      return;
    }
    
    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/invitations/verify`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ access_code: accessCode.trim() })
      });
      
      const data = await response.json();
      
      if (response.ok) {
        setInvitation(data);
        setStep(2);
        toast.success('Code verified successfully!');
      } else {
        toast.error(data.detail || 'Invalid access code');
      }
    } catch (error) {
      toast.error('Failed to verify code. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleRegister = async () => {
    if (!password) {
      toast.error('Please enter a password');
      return;
    }
    
    if (password.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }
    
    if (password !== confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }
    
    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/invitations/register`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          access_code: accessCode.trim(),
          password,
          mobile_number: mobileNumber,
          notification_preferences: notifications,
        })
      });
      
      const data = await response.json();
      
      if (response.ok) {
        // Store session token for persistent login
        localStorage.setItem('session_token', data.session_token);
        
        // Set user in auth context
        setUser({
          name: data.name,
          email: data.email,
          role: data.role || 'resident',
          hoa_id: data.hoa_id,
          unit_number: data.unit_number,
          building_name: data.building_name,
        });
        
        toast.success('Welcome to BodyCorp365!');
        setStep(4); // Success step
        
        // Redirect after short delay
        setTimeout(() => {
          navigate('/dashboard', { replace: true });
        }, 2000);
      } else {
        toast.error(data.detail || 'Registration failed');
      }
    } catch (error) {
      toast.error('Registration failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-blue-900 to-slate-900 flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        {/* Logo */}
        <div className="text-center mb-8">
          <Link to="/" className="inline-flex items-center gap-2">
            <Building2 className="w-10 h-10 text-blue-400" />
            <span className="text-2xl font-bold text-white">BodyCorp365</span>
          </Link>
        </div>

        {/* Step 1: Enter Code */}
        {step === 1 && (
          <Card className="border-0 shadow-2xl">
            <CardHeader className="text-center pb-2">
              <div className="mx-auto mb-4 p-4 bg-blue-100 rounded-full w-fit">
                <Key className="w-8 h-8 text-blue-600" />
              </div>
              <CardTitle className="text-2xl">Enter Invitation Code</CardTitle>
              <CardDescription>
                Enter the access code you received from your building trustee
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div>
                <Label htmlFor="code">Access Code</Label>
                <Input
                  id="code"
                  placeholder="BC365-12345"
                  value={accessCode}
                  onChange={(e) => setAccessCode(e.target.value.toUpperCase())}
                  className="text-center text-xl font-mono tracking-wider h-14"
                  data-testid="invitation-code-input"
                />
                <p className="text-xs text-slate-500 mt-2 text-center">
                  Format: BC365-XXXXX
                </p>
              </div>
              
              <Button 
                className="w-full h-12" 
                onClick={handleVerifyCode}
                disabled={loading}
                data-testid="verify-code-btn"
              >
                {loading ? (
                  <Loader2 className="w-5 h-5 animate-spin" />
                ) : (
                  <>
                    Verify Code
                    <ArrowRight className="w-5 h-5 ml-2" />
                  </>
                )}
              </Button>
              
              <div className="text-center">
                <Link to="/login" className="text-sm text-blue-600 hover:underline">
                  Already have an account? Sign in
                </Link>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Step 2: Confirm Details */}
        {step === 2 && invitation && (
          <Card className="border-0 shadow-2xl">
            <CardHeader className="text-center pb-2">
              <div className="mx-auto mb-4 p-4 bg-green-100 rounded-full w-fit">
                <CheckCircle2 className="w-8 h-8 text-green-600" />
              </div>
              <CardTitle className="text-2xl">Code Verified!</CardTitle>
              <CardDescription>
                Please confirm your details below
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="bg-slate-50 rounded-lg p-4 space-y-3">
                <div className="flex items-center gap-3">
                  <User className="w-5 h-5 text-slate-400" />
                  <div>
                    <p className="text-xs text-slate-500">Name</p>
                    <p className="font-medium">{invitation.resident_name}</p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <Building2 className="w-5 h-5 text-slate-400" />
                  <div>
                    <p className="text-xs text-slate-500">Building</p>
                    <p className="font-medium">{invitation.building_name}</p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <Home className="w-5 h-5 text-slate-400" />
                  <div>
                    <p className="text-xs text-slate-500">Unit</p>
                    <p className="font-medium">{invitation.unit_number}</p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <Mail className="w-5 h-5 text-slate-400" />
                  <div>
                    <p className="text-xs text-slate-500">Email</p>
                    <p className="font-medium">{invitation.email}</p>
                  </div>
                </div>
              </div>
              
              <div className="flex gap-3">
                <Button variant="outline" className="flex-1" onClick={() => setStep(1)}>
                  <ArrowLeft className="w-4 h-4 mr-2" />
                  Back
                </Button>
                <Button className="flex-1" onClick={() => setStep(3)}>
                  Continue
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Step 3: Set Password */}
        {step === 3 && (
          <Card className="border-0 shadow-2xl">
            <CardHeader className="text-center pb-2">
              <div className="mx-auto mb-4 p-4 bg-blue-100 rounded-full w-fit">
                <Lock className="w-8 h-8 text-blue-600" />
              </div>
              <CardTitle className="text-2xl">Create Your Account</CardTitle>
              <CardDescription>
                Set your password and preferences
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-5">
              <div>
                <Label htmlFor="password">Password *</Label>
                <Input
                  id="password"
                  type="password"
                  placeholder="Min 6 characters"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                />
              </div>
              
              <div>
                <Label htmlFor="confirmPassword">Confirm Password *</Label>
                <Input
                  id="confirmPassword"
                  type="password"
                  placeholder="Re-enter password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                />
              </div>
              
              <div>
                <Label htmlFor="mobile">Mobile Number (Optional)</Label>
                <Input
                  id="mobile"
                  placeholder="+27 82 123 4567"
                  value={mobileNumber}
                  onChange={(e) => setMobileNumber(e.target.value)}
                />
              </div>
              
              <div className="space-y-3">
                <Label>Notification Preferences</Label>
                <div className="space-y-2">
                  <div className="flex items-center space-x-2">
                    <Checkbox
                      id="notif-email"
                      checked={notifications.email}
                      onCheckedChange={(checked) => setNotifications({ ...notifications, email: checked })}
                    />
                    <label htmlFor="notif-email" className="text-sm flex items-center gap-2">
                      <Mail className="w-4 h-4" />
                      Email notifications
                    </label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <Checkbox
                      id="notif-push"
                      checked={notifications.push}
                      onCheckedChange={(checked) => setNotifications({ ...notifications, push: checked })}
                    />
                    <label htmlFor="notif-push" className="text-sm flex items-center gap-2">
                      <Bell className="w-4 h-4" />
                      Push notifications
                    </label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <Checkbox
                      id="notif-sms"
                      checked={notifications.sms}
                      onCheckedChange={(checked) => setNotifications({ ...notifications, sms: checked })}
                    />
                    <label htmlFor="notif-sms" className="text-sm flex items-center gap-2">
                      <Phone className="w-4 h-4" />
                      SMS notifications
                    </label>
                  </div>
                </div>
              </div>
              
              <div className="flex gap-3">
                <Button variant="outline" className="flex-1" onClick={() => setStep(2)}>
                  <ArrowLeft className="w-4 h-4 mr-2" />
                  Back
                </Button>
                <Button 
                  className="flex-1" 
                  onClick={handleRegister}
                  disabled={loading}
                  data-testid="complete-registration-btn"
                >
                  {loading ? (
                    <Loader2 className="w-5 h-5 animate-spin" />
                  ) : (
                    <>
                      Complete Registration
                      <CheckCircle2 className="w-4 h-4 ml-2" />
                    </>
                  )}
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Step 4: Success */}
        {step === 4 && (
          <Card className="border-0 shadow-2xl">
            <CardContent className="p-8 text-center">
              <div className="mx-auto mb-6 p-6 bg-green-100 rounded-full w-fit">
                <CheckCircle2 className="w-12 h-12 text-green-600" />
              </div>
              <h2 className="text-2xl font-bold text-slate-900 mb-2">Welcome to BodyCorp365!</h2>
              <p className="text-slate-600 mb-6">
                Your account has been created successfully. You now have access to:
              </p>
              <div className="text-left bg-slate-50 rounded-lg p-4 mb-6">
                <ul className="space-y-2 text-sm">
                  <li className="flex items-center gap-2">
                    <CheckCircle2 className="w-4 h-4 text-green-500" />
                    Building notices and announcements
                  </li>
                  <li className="flex items-center gap-2">
                    <CheckCircle2 className="w-4 h-4 text-green-500" />
                    Maintenance request system
                  </li>
                  <li className="flex items-center gap-2">
                    <CheckCircle2 className="w-4 h-4 text-green-500" />
                    Building documents
                  </li>
                  <li className="flex items-center gap-2">
                    <CheckCircle2 className="w-4 h-4 text-green-500" />
                    Trustee communications
                  </li>
                </ul>
              </div>
              <p className="text-sm text-slate-500">
                Redirecting to your dashboard...
              </p>
            </CardContent>
          </Card>
        )}

        {/* Footer */}
        <p className="text-center text-slate-400 text-sm mt-6">
          © 2026 BodyCorp365. All rights reserved.
        </p>
      </div>
    </div>
  );
}
